import { Engine, ArcRotateCamera, Vector3 as BVector3, DefaultRenderingPipeline } from '@babylonjs/core';
import { SceneManager } from './scene-manager.js';
import { CameraController } from '../camera/camera-controller.js';
import type { EngineMetrics } from '../ui/debug-overlay.js';

export class GameEngine {
  public readonly engine: Engine;
  public readonly sceneManager: SceneManager;
  public readonly cameraController: CameraController;
  public readonly camera: ArcRotateCamera;
  public readonly pipeline: DefaultRenderingPipeline | null = null;
  private resizeHandler: () => void;
  private lastFrameTime: number = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.engine = new Engine(canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
      powerPreference: 'high-performance'
    });

    this.sceneManager = new SceneManager(this.engine);
    this.cameraController = new CameraController({
      initialRadius: 16,
      minRadius: 4,
      maxRadius: 45
    });

    this.camera = new ArcRotateCamera(
      'playgroundCamera',
      this.cameraController.alpha,
      this.cameraController.beta,
      this.cameraController.radius,
      new BVector3(0, 0, 0),
      this.sceneManager.scene
    );
    this.camera.attachControl(canvas, true);
    this.cameraController.attachBabylonCamera(this.camera);

    try {
      this.pipeline = new DefaultRenderingPipeline('defaultPipeline', true, this.sceneManager.scene, [this.camera]);
      this.pipeline.bloomEnabled = true;
      this.pipeline.bloomThreshold = 0.8;
      this.pipeline.bloomWeight = 0.35;
      this.pipeline.bloomKernel = 64;
      this.pipeline.bloomScale = 0.5;
    } catch {
      this.pipeline = null;
    }

    this.resizeHandler = () => {
      this.engine.resize();
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  public get scene() {
    return this.sceneManager.scene;
  }

  public getMetrics(): EngineMetrics {
    const scene = this.sceneManager.scene;
    const fps = this.engine.getFps();
    const frameTimeMs = fps > 0 ? 1000 / fps : 0;
    return {
      fps,
      frameTimeMs,
      drawCalls: this.engine._drawCalls.current,
      totalVertices: scene.totalVerticesPerfCounter.current,
      activeMeshes: scene.getActiveMeshes().length
    };
  }

  public start(onTick?: (metrics: EngineMetrics, dt: number) => void): void {
    this.lastFrameTime = performance.now();
    this.engine.runRenderLoop(() => {
      const now = performance.now();
      const dt = Math.min((now - this.lastFrameTime) / 1000, 0.1);
      this.lastFrameTime = now;

      if (onTick) {
        onTick(this.getMetrics(), dt);
      }

      this.cameraController.update(dt);
      this.sceneManager.scene.render();
    });
  }

  public stop(): void {
    this.engine.stopRenderLoop();
  }

  public dispose(): void {
    window.removeEventListener('resize', this.resizeHandler);
    this.camera.dispose();
    this.sceneManager.dispose();
    this.engine.dispose();
  }
}
